import {MbscCalendarEvent} from "@mobiscroll/react";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {RESERVATIONS_QUERY_KEY} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {useMemo} from "react";
import {useQuery} from "react-query";
import type {ReservationRailSearchState} from "../../rail/search/ReservationRailSearch";
import {useReservationRailSearch} from "../../rail/search/useReservationRailSearch";
import {EventGenerationConfig, generateReservationEvents} from "./calendarEventGenerator";

// Connects the shared rail search controller to reservation data and Mobiscroll events.
interface UseCalendarRailSearchResultsParams {
    sector?: Sector;
    fromDate: Date;
    toDate: Date;
    selectedStatuses: ReservationStatus[];
    selectedResourceIds: string[];
    eventGenerationConfig: EventGenerationConfig;
    reservationEvents: MbscCalendarEvent[];
}

export function useCalendarRailSearchResults({
    sector,
    fromDate,
    toDate,
    selectedStatuses,
    selectedResourceIds,
    eventGenerationConfig,
    reservationEvents,
}: UseCalendarRailSearchResultsParams) {
    const search = useReservationRailSearch();
    const sectorId = sector?.id ?? "";

    // Normalize filters so equivalent selections share the same query key.
    const normalizedStatuses = useMemo(
        () => [...selectedStatuses].sort(),
        [selectedStatuses]
    );
    const normalizedResourceIds = useMemo(
        () => [...selectedResourceIds].sort(),
        [selectedResourceIds]
    );
    const statusKey = normalizedStatuses.join("-");
    const resourceKey = normalizedResourceIds.join("-");

    // Fetch only after the query is debounced; weekly planner has no reservation search.
    const query = useQuery<Reservation[]>({
        queryKey: [
            RESERVATIONS_QUERY_KEY,
            "calendar-rail-search",
            sectorId,
            fromDate.getTime(),
            toDate.getTime(),
            statusKey,
            resourceKey,
            search.debouncedQuery,
        ],
        enabled: Boolean(
            sectorId
            && search.debouncedQuery
            && sector?.sectorType !== SectorType.WEEKLY_PLANNER
        ),
        keepPreviousData: true,
        queryFn: () => ReservationService.searchReservationsByProfile({
            query: search.debouncedQuery,
            overlaps: {start: fromDate, end: toDate},
            sectorIds: [sectorId],
            resourceIds: normalizedResourceIds.length
                ? normalizedResourceIds
                : undefined,
            statuses: normalizedStatuses,
            limit: 50,
        }),
    });

    // Keep rail results in chronological order.
    const reservations = useMemo(
        () => [...(query.data ?? [])].sort((left, right) => {
            const leftStart = left.expectedStartDate
                ? new Date(left.expectedStartDate).getTime()
                : Number.MAX_SAFE_INTEGER;
            const rightStart = right.expectedStartDate
                ? new Date(right.expectedStartDate).getTime()
                : Number.MAX_SAFE_INTEGER;
            return leftStart - rightStart;
        }),
        [query.data]
    );

    // Prefer existing calendar events and generate only missing search events.
    const eventsByReservationId = useMemo(() => {
        const result = new Map<string, MbscCalendarEvent>();
        const generatedSearchEvents = generateReservationEvents(
            reservations,
            eventGenerationConfig
        );

        [...reservationEvents, ...generatedSearchEvents].forEach(event => {
            const reservationId = event.reservationId;
            if (typeof reservationId === "string" && !result.has(reservationId)) {
                result.set(reservationId, event);
            }
        });

        return result;
    }, [eventGenerationConfig, reservationEvents, reservations]);

    // Expose React Query progress through the shared rail search states.
    let state: ReservationRailSearchState;
    if (!search.query.trim() || !search.debouncedQuery) {
        state = "idle";
    } else if (query.isLoading) {
        state = "loading";
    } else if (query.isError) {
        state = "error";
    } else if (query.isFetching) {
        state = "refreshing";
    } else if (reservations.length === 0) {
        state = "empty";
    } else {
        state = "results";
    }

    return {
        search,
        query,
        reservations,
        eventsByReservationId,
        state,
    };
}
